import * as yup from "yup";
import {
  CAPITAL_LATIN_AND_NUMBERS_ONLY,
  CAPITAL_LATIN_ONLY,
  EMAIL_LIST_WITH_COMMA,
  NUMBERS_ONLY,
  NUMBERS_ONLY_WITH_EMPTY,
  NUM_RANGE_FROM_1_TO_999999,
  PHONE_REG_EXP,
} from "../../utils/regexps";

export const yupValidations = (t: any) => ({
  email: yup
    .string()
    .email(t`Must be a valid email`)
    .max(255)
    .required(t`Email is required`),
  emailNotRequired: yup
    .string()
    .email(t`Must be a valid email`)
    .max(255)
    .notRequired(),
  password: yup
    .string()
    .min(6, t`6 characters minimum`)
    .max(255)
    .required(t`Password is required`),
  requiredString: yup
    .string()
    .required(t`Field is required`)
    .matches(/[^\s]+/, t`Must contain at least one non-whitespace character`),
  requiredSelectedCampaign: yup.object().shape({
    label: yup
      .string()
      .required(t`Please select a previous campaign to relates a new versions`),
    value: yup
      .string()
      .required(t`Please select a previous campaign to relates a new versions`),
  }),
  phoneNumber: yup
    .string()
    .matches(PHONE_REG_EXP, t`Phone number is not valid`),
  // Same rule, but a blank value passes - for forms where the phone is genuinely optional.
  // Kept separate: relaxing `phoneNumber` in place would make every other form's phone optional.
  phoneNumberNotRequired: yup
    .string()
    .matches(PHONE_REG_EXP, {
      message: t`Phone number is not valid`,
      excludeEmptyString: true,
    }),
  selectRequired: yup.object().shape({
    label: yup.string().required(t`Field is required`),
    value: yup.string().required(t`Field is required`),
  }),
  multiSelectRequired: yup
    .array(
      yup.object().shape({
        label: yup.string().required(t`Field is required`),
        value: yup.string().required(t`Field is required`),
      })
    )
    .min(1, t`Field is required`),
  selectAdAgRequired: yup.object().shape({
    label: yup
      .string()
      .required(
        t`If this campaign has no Advertising agency, please select the option of "_ No Ad Agency - לא קיים משרד פרסום _"`
      ),
    value: yup
      .string()
      .required(
        t`If this campaign has no Advertising agency, please select the option of "_ No Ad Agency - לא קיים משרד פרסום _"`
      ),
  }),
  passwordConfirm: yup
    .string()
    .oneOf([yup.ref("Password"), null], t`Passwords must match`),
  requiredBrandId: yup
    .string()
    .min(2, t`2 characters minimum`)
    .max(10, t`10 characters maximum`)
    .matches(/\S+$/, t`Using spaces in ADID is not allowed`)
    .matches(CAPITAL_LATIN_ONLY, t`Only capital latin letters without spaces`)
    .required(t`Field is required`),
  requiredMax60: yup
    .string()
    .required(t`Field is required`)
    .max(60, ({ value }) => `${value.length}/60 characters maximum`),
  requiredMax45: yup
    .string()
    .required(t`Field is required`)
    .max(45, ({ value }) => `${value.length}/45 chars maximum`),
  requiredMax40: yup
    .string()
    .required(t`Field is required`)
    .max(40, ({ value }) => `${value.length}/40 chars maximum`),
  max16: yup
    .string()
    .max(16, ({ value }) => `${value.length}/16 chars maximum`),
  max35: yup
    .string()
    .max(35, ({ value }) => `${value.length}/35 chars maximum`),
  requiredEmailListWithComma: yup
    .string()
    .required(t`Field is required`)
    .matches(
      EMAIL_LIST_WITH_COMMA,
      t`Must be a valid email or valid email list separated by comma`
    ),
  requiredNumber: yup
    .string()
    .matches(NUMBERS_ONLY, "Must contains numbers only")
    .required(t`Field is required`),
  requiredFloatNumber: yup
    .string()
    .matches(/^[0-9]+(\.[0-9]+)?$/, "Must contains numbers only")
    .required(t`Field is required`),
  capitaLatinAndNumbers: yup
    .string()
    .matches(
      CAPITAL_LATIN_AND_NUMBERS_ONLY,
      t`Must contains capital latin letters, numbers, hyphens and underscores only`
    ),
  requiredNumberMin1: yup
    .string()
    .matches(NUM_RANGE_FROM_1_TO_999999, t`1 sec min`)
    .required(t`Field is required`),
  clientDiscount: yup
    // Number between 0 and 50 or empty string
    .string()
    .matches(NUMBERS_ONLY_WITH_EMPTY, t`Must contains numbers only`)
    .test(
      "is-valid-discount",
      t`Discount must be between 0 and 50`,
      (value) => {
        if (value === "") return true;
        return Number(value) >= 0 && Number(value) <= 50;
      }
    ),
  requiredStringWithNoCommaAndSemicolon: yup
    .string()
    .required(t`Field is required`)
    .matches(
      /^[^,;]+$/,
      t`The use of a comma (,) or a semicolon (;) chars is not allowed.`
    ),
  notRequiredStringWithNoCommaAndSemicolon: yup
    .string()
    .matches(
      /^[^,;]+$/,
      t`The use of a comma (,) or a semicolon (;) chars is not allowed.`
    ),
  brandName: yup
    .string()
    .required(t`Field is required`)
    .matches(
      /^[^,;]+$/,
      t`The use of a comma (,) or a semicolon (;) chars is not allowed.`
    )
    .max(40, ({ value }) => `${value.length}/40 chars maximum`),
  // Spec 17 - the "create new media company" popup. Same rule as brandName but a 100-char ceiling:
  // client names in this DB already run to 47 chars and the admin client form (CreateEditClient)
  // caps Name at nothing at all, so brandName's 40 is too tight for a company name. Kept as its own
  // schema so Brand and Advertiser stay at 40.
  // All four carry a .trim() transform: it runs before the tests, so surrounding whitespace can
  // neither fail an anchored regex (PHONE_REG_EXP, email) nor count toward a max, and the value
  // the form submits is already trimmed. A whitespace-only company name correctly fails required.
  requiredMax100WithNoCommaAndSemicolon: yup
    .string()
    .trim()
    .required(t`Field is required`)
    .matches(
      /^[^,;]+$/,
      t`The use of a comma (,) or a semicolon (;) chars is not allowed.`
    )
    .max(100, ({ value }) => `${value.length}/100 chars maximum`),
  // Optional free text. Backs the media-company contact name, which is otherwise deliberately
  // unvalidated - the cap exists only to stay under ClientInfoManager.CheckItem's 200-char throw.
  max100: yup
    .string()
    .trim()
    .max(100, ({ value }) => `${value.length}/100 chars maximum`),
  // As emailNotRequired, but 100 rather than 255 - the shared one allows more than the BL accepts.
  emailNotRequiredMax100: yup
    .string()
    .trim()
    .email(t`Must be a valid email`)
    .max(100, ({ value }) => `${value.length}/100 chars maximum`)
    .notRequired(),
  // As phoneNumberNotRequired, plus a 10-digit ceiling. Separators (- space + parentheses) are
  // still accepted by PHONE_REG_EXP and are not counted, so "050-1234567" passes at 10 digits.
  phoneNumberNotRequiredMax10Digits: yup
    .string()
    .trim()
    .matches(PHONE_REG_EXP, {
      message: t`Phone number is not valid`,
      excludeEmptyString: true,
    })
    .test(
      "max-10-digits",
      t`Phone number must contain at most 10 digits`,
      (value) => !value || (value.match(/\d/g) || []).length <= 10
    ),
});

export enum ValidatorsE {
  EMAIL = "email",
  EMAIL_NOT_REQUIRED = "emailNotRequired",
  EMAIL_LIST = "requiredEmailListWithComma",
  PASSWORD = "password",
  PASSWORD_CONFIRM = "passwordConfirm",
  REQUIRED_STRING = "requiredString",
  PHONE_NUMBER = "phoneNumber",
  PHONE_NUMBER_NOT_REQUIRED = "phoneNumberNotRequired",
  SELECT_REQUIRED = "selectRequired",
  MULTI_SELECT_REQUIRED = "multiSelectRequired",
  SELECT_AD_AG_REQUIRED = "selectAdAgRequired",
  REQUIRED_BRAND_ID = "requiredBrandId",
  REQUIRED_MAX_60 = "requiredMax60",
  REQUIRED_MAX_45 = "requiredMax45",
  REQUIRED_MAX_40 = "requiredMax40",
  MAX_16 = "max16",
  MAX_35 = "max35",
  REQUIRED_NUMBER = "requiredNumber",
  REQUIRED_NUMBER_MIN_1 = "requiredNumberMin1",
  REQUIRED_SELECTED_CAMPAIGN = "requiredSelectedCampaign",
  CAPITAL_LATIN_AND_NUMBERS = "capitaLatinAndNumbers",
  REQUIRED_STRING_WITH_NO_COMMA_AND_SEMICOLON = "requiredStringWithNoCommaAndSemicolon",
  NOT_REQUIRED_STRING_WITH_NO_COMMA_AND_SEMICOLON = "notRequiredStringWithNoCommaAndSemicolon",
  CLIENT_DISCOUNT = "clientDiscount",
  BRAND_NAME = "brandName",
  REQUIRED_MAX_100_WITH_NO_COMMA_AND_SEMICOLON = "requiredMax100WithNoCommaAndSemicolon",
  MAX_100 = "max100",
  EMAIL_NOT_REQUIRED_MAX_100 = "emailNotRequiredMax100",
  PHONE_NUMBER_NOT_REQUIRED_MAX_10_DIGITS = "phoneNumberNotRequiredMax10Digits",
  REQUIRED_FLOAT_NUMBER = "requiredFloatNumber",
}
